import handlebars from "handlebars";
import { getRawDraftStateWithReplacements } from "../functions.js";
const TOKEN_RE = /\{\{\s*([a-zA-Z_][\w.]*)\s*\}\}/g;
// Renderer for HTML targets — Handlebars escapes the resolved values, so an
// organisation named `A & B` is substituted as `A &amp; B`.
export function renderTemplate(body, ctx) {
    if (!body)
        return "";
    return handlebars.compile(body)(ctx);
}
// Renderer for plain-text targets such as an email subject header, where an
// escaped value would reach the recipient as literal `&amp;` / `&#x27;`.
export function renderTemplateText(body, ctx) {
    if (!body)
        return "";
    return handlebars.compile(body, { noEscape: true })(ctx);
}
// Draft.js-aware renderer — used by document scopes that store Draft.js JSON
// (invoice / purchase order / time report / material list).
//
// Why this exists separately from `renderTemplate`: Draft.js raw content state
// stores `blocks[].text` strings alongside offset-tracked metadata
// (`inlineStyleRanges`, `entityRanges`) for bold spans, links, etc. A naive
// `handlebars.compile(bodyJson)(ctx)` would rewrite the JSON's text but leave
// the style/entity offsets pointing at the wrong characters, corrupting
// formatting whenever a replacement value's length differs from the token's
// length. So instead we extract tokens, resolve each via Handlebars, and let
// Draft.js's `Modifier.replaceText` perform the substitution with automatic
// offset adjustment (in `getRawDraftStateWithReplacements`).
//
// `bodyJson` is the org-level template (stringified Draft.js raw content).
// `fallbackPlainText` is the Tolgee default used when the org template is
// missing or invalid. Returns a RawDraftContentState with all `{{path}}`
// tokens substituted from `ctx`. Caller `JSON.stringify`s the result.
export function renderDraftJsTemplate(bodyJson, fallbackPlainText, ctx) {
    const replacements = collectReplacements(bodyJson, fallbackPlainText, ctx);
    return getRawDraftStateWithReplacements(bodyJson, fallbackPlainText, replacements);
}
function collectReplacements(bodyJson, fallbackPlainText, ctx) {
    // Key by the exact token spelling found (`match[0]`), not the normalized
    // `{{id}}` — the Draft.js path replaces literally, so a spaced token like
    // `{{ client.name }}` must be matched and replaced as written.
    const result = {};
    for (const source of [bodyJson, fallbackPlainText]) {
        if (!source)
            continue;
        TOKEN_RE.lastIndex = 0;
        let match;
        while ((match = TOKEN_RE.exec(source)) !== null) {
            const token = match[0];
            const id = match[1];
            if (id && !(token in result)) {
                result[token] = handlebars.compile(`{{${id}}}`)(ctx);
            }
        }
    }
    return result;
}
//# sourceMappingURL=renderer.js.map